import app from '@/main.js';
import {useUserStore} from '@/stores/user';
import {eventBus} from "@/util/eventBus";
import TencentCloudChat from '@tencentcloud/chat';
import TIMUploadPlugin from 'tim-upload-plugin';
import {ElMessage, ElMessageBox} from 'element-plus'
import {imUserSig} from "@/api/chat";

const SIG_CACHE_KEY = 'im_user_sig'
// Refresh sig when less than 10 minutes remain
const SIG_REFRESH_THRESHOLD_MS = 10 * 60 * 1000

let that;

let store = null;

export let chat = null;

// Cache the last synced profile to avoid redundant updateMyProfile calls
let lastSyncedProfileKey = '';

// Read cached UserSig if still valid
const readCachedSig = () => {
    try {
        const raw = localStorage.getItem(SIG_CACHE_KEY)
        if (!raw) return null
        const data = JSON.parse(raw)
        if (!data || !data.userSig || !data.sdkAppId || !data.imId) return null
        if (data.expireAt - Date.now() > SIG_REFRESH_THRESHOLD_MS) return data
    } catch (e) {
        console.warn('Read IM sig cache failed', e)
    }
    return null
}

const writeCachedSig = (resp) => {
    try {
        localStorage.setItem(SIG_CACHE_KEY, JSON.stringify({
            userSig: resp.userSig,
            sdkAppId: resp.sdkAppId,
            imId: resp.imId,
            expireAt: Date.now() + (Number(resp.expire) || 0) * 1000,
        }))
    } catch (e) {
        console.warn('Write IM sig cache failed', e)
    }
}

const clearCachedSig = () => {
    try {
        localStorage.removeItem(SIG_CACHE_KEY)
    } catch (e) {
        console.warn('Clear IM sig cache failed', e)
    }
}

// In-flight de-duplication: concurrent callers share the same Promise
let inflightSigPromise = null

// Fetch a fresh UserSig from server. Throws when backend says no.
const fetchUserSigFromServer = async () => {
    if (inflightSigPromise) return inflightSigPromise
    inflightSigPromise = (async () => {
        try {
            const res = await imUserSig()
            if (!res || res.code !== 200 || !res.data) {
                const err = new Error(res?.msg || 'IM 服务暂不可用，请稍后再试')
                err.code = res?.code
                throw err
            }
            writeCachedSig(res.data)
            return res.data
        } finally {
            inflightSigPromise = null
        }
    })()
    return inflightSigPromise
}

export const initTIM = async (_this) => {
    that = _this
    store = useUserStore()
    // Skip until user has logged in (we need JWT to call sys/im/userSig)
    if (!store.userToken) return
    // Already wired up
    if (chat && (that.$im || (that.$ && that.$.appContext.app.config.globalProperties.$im))) return
    // Startup: only restore from cache. Skip if no cache so we don't burn rate-limit quota.
    // Real loginIM will fire later when the user actually enters an IM scene.
    const cached = readCachedSig()
    if (!cached) return
    await loginWithSig(cached)
}

// Create the chat instance lazily, once we know sdkAppId from server
const ensureChatInstance = (sdkAppId) => {
    if (chat) return
    chat = TencentCloudChat.create({SDKAppID: Number(sdkAppId)})
    chat.setLogLevel(1)
    chat.registerPlugin({'tim-upload-plugin': TIMUploadPlugin})
    const appInstance = that.$.appContext.app
    appInstance.config.globalProperties.$im = chat
    appInstance.config.globalProperties.$TencentCloudChat = TencentCloudChat
}

// On-demand login: cache hit fast path, else hit server.
// Called when the user enters an IM scene (chat list, contact seller, etc.)
export const loginIM = async () => {
    if (!store) store = useUserStore()
    if (!store.userToken) return

    let sig = readCachedSig()
    if (!sig) {
        try {
            sig = await fetchUserSigFromServer()
        } catch (err) {
            handleSigError(err)
            return
        }
    }
    await loginWithSig(sig)
}

// Use the given sig to drive chat.login. Clears cache on failure for self-healing.
const loginWithSig = async (sig) => {
    const {userSig, sdkAppId, imId} = sig

    // Treat server-issued imId as the single source of truth to avoid drift
    if (!store) store = useUserStore()
    if (store.imId !== imId) store.imId = imId

    ensureChatInstance(sdkAppId)

    // Register events before login so we never miss SDK_READY on repeat login
    chat.on(TencentCloudChat.EVENT.SDK_READY, SDKReady)
    chat.on(TencentCloudChat.EVENT.KICKED_OUT, OutTips)
    chat.on(TencentCloudChat.EVENT.SDK_NOT_READY, imNotREADY)

    const currentLogged = chat.getLoginUser()
    if (currentLogged === imId) {
        // Repeat login won't fire SDK_READY again; check synchronously
        if (chat.isReady()) SDKReady()
        return
    }
    if (currentLogged && currentLogged !== imId) await chat.logout()

    try {
        const imResponse = await chat.login({userID: imId, userSig})
        if (imResponse?.data?.repeatLogin === true && chat.isReady()) SDKReady()
    } catch (imError) {
        console.warn('IM login failed, clear cache to avoid loop:', imError)
        clearCachedSig()
    }
}

const handleSigError = (err) => {
    const msg = err?.message || ''
    if (msg.includes('请重新登录')) {
        // JWT expired — let the standard logout path drive the user back to login
        if (!store) store = useUserStore()
        store.logout()
        return
    }
    // "IM 身份未初始化" / "IM 服务未配置" / 其他
    if (msg) ElMessage.warning(msg)
}

const SDKReady = () => {
    if (!store) store = useUserStore()
    store.imReady = true
    // Sync business profile (nickname/avatar) into IM so peers see proper info
    syncProfileToIM()
    // 获取单聊和群聊会话的未读总数
    const count = chat.getTotalUnreadMessageCount()
    eventBus.emit('totalUnreadMessageCountUpdated', count)
    // 监听单聊和群聊会话的未读总数
    chat.on(TencentCloudChat.EVENT.TOTAL_UNREAD_MESSAGE_COUNT_UPDATED, unread)
}

// Sync nickname/avatar from business store (or explicit profile) into IM user profile.
// Safe to call multiple times: skips when nothing changed since last sync.
export const syncProfileToIM = async (profile) => {
    if (!chat || !chat.isReady()) return
    if (!store) store = useUserStore()
    const source = profile || store.user || {}
    const nick = source.nickname || ''
    const avatar = source.avatarPath || ''
    const key = `${nick}|${avatar}`
    if (key === lastSyncedProfileKey) return
    try {
        await chat.updateMyProfile({nick, avatar})
        lastSyncedProfileKey = key
    } catch (err) {
        console.warn('IM profile sync failed', err)
    }
}

function unread(event) {
    eventBus.emit('totalUnreadMessageCountUpdated', event?.data || 0)
}

const imNotREADY = () => {
    console.log('imNotREADY')
}

const OutTips = async () => {
    // Clear cache immediately so the bad sig can't drive another login attempt,
    // regardless of whether the user dismisses the dialog
    clearCachedSig()
    const currentLogged = chat?.getLoginUser()
    if (currentLogged) await chat.logout()
    ElMessageBox.confirm('您已被强制下线', '登录异常', {
        type: 'warning',
        showClose: false,
        showCancelButton: false,
        closeOnClickModal: false,
        closeOnPressEscape: false,
    }).then(() => {
        if (!store) store = useUserStore()
        store.logout()
    }).catch((e) => {
        console.log(e)
    })
}

// 取消监听并登出 IM（用户主动退出登录时调用）
export const cancelMonitoring = () => {
    const globalProps = app.config.globalProperties
    if (globalProps.$im) {
        globalProps.$im.off(globalProps.$TencentCloudChat.EVENT.SDK_READY, SDKReady)
        globalProps.$im.off(globalProps.$TencentCloudChat.EVENT.KICKED_OUT, OutTips)
        globalProps.$im.off(globalProps.$TencentCloudChat.EVENT.SDK_NOT_READY, imNotREADY)
        globalProps.$im.off(globalProps.$TencentCloudChat.EVENT.TOTAL_UNREAD_MESSAGE_COUNT_UPDATED, unread)
        globalProps.$im.logout()
        globalProps.$im = null
        globalProps.$TencentCloudChat = null
    }
    chat = null
    lastSyncedProfileKey = ''
    clearCachedSig()
}
